/**
 * Slice 19 — Issue triage metadata on maintenance_issue_reports.
 * Status lives in `status`; path/assignee/claim draft live in `resolution_note` JSON.
 */

export type IssueTriagePath = "restock" | "vendor" | "claim" | "dismiss";

export type IssueTriageStatus = "open" | "in_progress" | "claiming" | "resolved";

export interface IssueTriageMeta {
  v: 1;
  path?: IssueTriagePath | null;
  assignee_name?: string | null;
  assignee_id?: string | null;
  claim_draft?: string | null;
  done_line?: string | null;
}

export function parseIssueTriageMeta(raw: string | null | undefined): IssueTriageMeta {
  if (!raw?.trim()) return { v: 1 };
  const t = raw.trim();
  if (!t.startsWith("{")) return { v: 1 };
  try {
    const parsed = JSON.parse(t) as IssueTriageMeta;
    if (parsed && parsed.v === 1) return parsed;
  } catch {
    /* plain-text legacy note */
  }
  return { v: 1 };
}

export function serializeIssueTriageMeta(meta: IssueTriageMeta): string {
  return JSON.stringify({ ...meta, v: 1 as const });
}

/** Normalize DB status string → triage status. */
export function normalizeIssueStatus(status: string | null | undefined): IssueTriageStatus {
  const s = (status || "open").toLowerCase();
  if (s === "resolved" || s === "done") return "resolved";
  if (s === "claiming" || s === "claim") return "claiming";
  if (s === "in_progress" || s === "in progress" || s === "progress") return "in_progress";
  return "open";
}

export function isIssueOpen(status: string | null | undefined): boolean {
  return normalizeIssueStatus(status) !== "resolved";
}

export function issueTypeLabel(response: string | null | undefined): "Missing" | "Broken" {
  return response === "needs_repair" ? "Broken" : "Missing";
}

/** List / detail status chip label. */
export function issueStatusLabel(
  status: string | null | undefined,
  path?: IssueTriagePath | null,
): string {
  const s = normalizeIssueStatus(status);
  if (s === "resolved") return "Done";
  if (s === "claiming") return "Claiming";
  if (s === "in_progress") {
    if (path === "restock") return "In progress · Restock";
    if (path === "vendor") return "In progress · Vendor";
    return "In progress";
  }
  return "Open";
}

export function buildClaimDraft(input: {
  note: string;
  room?: string | null;
  type: "Missing" | "Broken";
  cleanLabel?: string | null;
  guestName?: string | null;
  guestStayLabel?: string | null;
}): string {
  const what = (input.note || "").trim() || (input.type === "Broken" ? "Item" : "Item");
  const roomBit = input.room?.trim() && !what.toLowerCase().includes(input.room.trim().toLowerCase())
    ? ` in ${input.room.trim()}`
    : "";
  const cleanBit = input.cleanLabel?.trim() || "turnover";
  const verb = input.type === "Broken" ? "found broken" : "reported missing";
  const guest =
    input.guestName?.trim()
      ? `, after ${input.guestName.trim()}${input.guestStayLabel ? ` (${input.guestStayLabel})` : ""}`
      : "";
  return `${what}${roomBit} ${verb} at ${cleanBit}${guest}. Photo attached. Requesting replacement cost.`;
}

export function buildDoneLine(input: {
  path?: IssueTriagePath | null;
  assigneeName?: string | null;
  whenLabel?: string | null;
}): string {
  const when = input.whenLabel?.trim() || "";
  const who = input.assigneeName?.trim();
  if (input.path === "claim") {
    return who ? `Claim closed · ${who}${when ? ` · ${when}` : ""}` : `Claim closed${when ? ` · ${when}` : ""}`;
  }
  if (input.path === "vendor") {
    return who ? `Repaired · ${who}${when ? ` · ${when}` : ""}` : `Repaired · vendor${when ? ` · ${when}` : ""}`;
  }
  if (input.path === "dismiss") return `Dismissed${when ? ` · ${when}` : ""}`;
  return who ? `Restocked · ${who}${when ? ` · ${when}` : ""}` : `Restocked${when ? ` · ${when}` : ""}`;
}

/** Airbnb host resolutions (no API file — deep-link only). */
export const AIRBNB_RESOLUTIONS_URL = "https://www.airbnb.com/resolutions";

export function airbnbClaimUrl(externalId?: string | null): string {
  const id = (externalId || "").trim();
  // Hospitable/Airbnb confirmation-style codes → reservation details when possible
  if (id && /^[A-Z0-9]{8,}$/i.test(id) && !id.includes("-")) {
    return `https://www.airbnb.com/hosting/reservations/details/${encodeURIComponent(id)}`;
  }
  return AIRBNB_RESOLUTIONS_URL;
}
